import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Import the new ErrorBoundary
import ErrorBoundary from './components/ErrorBoundary';

import PublicPageRoute from './components/public/PublicPageRoute';
import PublicPreviewRoute from './components/public/PublicPreviewRoute';
import { useHoldingPageSettings } from './hooks/useHoldingPageSettings';

// Lazy load admin components (only loaded when /admin routes are accessed)
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const Dashboard = lazy(() => import('./components/admin/Dashboard'));
const PageLayoutEditor = lazy(() => import('./components/admin/PageLayoutEditor'));
const PagesManager = lazy(() => import('./components/admin/PagesManager'));
const DesignSystemEditor = lazy(() => import('./components/admin/DesignSystemEditor'));
const FinishTypesEditor = lazy(() => import('./components/admin/FinishTypesEditor'));
const EnquiriesManager = lazy(() => import('./components/admin/EnquiriesManager'));
const SeasonalEffectsEditor = lazy(() => import('./components/admin/SeasonalEffectsEditor'));
const ColorStudio = lazy(() => import('./components/admin/ColorStudio'));
const ViewerPipelineEditor = lazy(() => import('./components/admin/ViewerPipelineEditor'));
const LightingControls = lazy(() => import('./components/admin/LightingControls'));
const CurtainSettings = lazy(() => import('./components/admin/CurtainSettings'));
const SettingsEditor = lazy(() => import('./components/admin/SettingsEditor'));
const HoldingPageEditor = lazy(() => import('./components/admin/HoldingPageEditor'));
const NavigationEditor = lazy(() => import('./components/admin/NavigationEditor'));
const PricingEditor = lazy(() => import('./components/admin/PricingEditor'));
const ProductManager = lazy(() => import('./components/admin/ProductManager'));
const AssetLibrary = lazy(() => import('./components/admin/AssetLibrary'));
const MaterialPropertiesEditor = lazy(() => import('./components/admin/MaterialPropertiesEditor'));
const MobileExperienceEditor = lazy(() => import('./components/admin/MobileExperienceEditor'));
const AccessibilityEditor = lazy(() => import('./components/admin/AccessibilityEditor'));
const SEOSettingsEditor = lazy(() => import('./components/admin/SEOSettingsEditor'));
const AnalyticsEditor = lazy(() => import('./components/admin/AnalyticsEditor'));
const SocialMediaEditor = lazy(() => import('./components/admin/SocialMediaEditor'));
const EmailMarketingEditor = lazy(() => import('./components/admin/EmailMarketingEditor'));
const PerformanceMonitor = lazy(() => import('./components/admin/PerformanceMonitor'));
const SecuritySettingsEditor = lazy(() => import('./components/admin/SecuritySettingsEditor'));
const BackupRecoveryEditor = lazy(() => import('./components/admin/BackupRecoveryEditor'));
const HomeSectionEditorRedirect = lazy(() => import('./components/admin/HomeSectionEditorRedirect'));
const ImportColours = lazy(() => import('./components/ImportColours'));
const EntranceHeroRoute = lazy(() => import('./components/entrance/EntranceHeroRoute'));
const VaseStage3DPrototypeRoute = lazy(() => import('./components/prototypes/VaseStage3DPrototypeRoute'));
function App() {
  const { settings: holdingPageSettings } = useHoldingPageSettings('published');
  const holdingPageEnabled = holdingPageSettings.enabled;

  return (
    // Wrap the entire application router with the ErrorBoundary
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          {/* Admin routes - lazy loaded for better initial load performance */}
          <Route
            path="/admin/*"
            element={
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-stone-50">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-stone-900 mb-4"></div>
                      <p className="text-stone-600">Loading admin panel...</p>
                    </div>
                  </div>
                }
              >
                <AdminLayout />
              </Suspense>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="hero"
              element={<HomeSectionEditorRedirect section="hero" label="Hero" />}
            />
            <Route
              path="about"
              element={<HomeSectionEditorRedirect section="about" label="About" />}
            />
            <Route
              path="process"
              element={<HomeSectionEditorRedirect section="process" label="Process" />}
            />
            <Route path="layout" element={<PageLayoutEditor />} />
            <Route path="pages" element={<PagesManager />} />
            <Route path="design" element={<DesignSystemEditor />} />
            <Route path="navigation" element={<NavigationEditor />} />
            <Route
              path="subscribe"
              element={<HomeSectionEditorRedirect section="subscribe" label="Subscribe" />}
            />
            <Route path="enquiries" element={<EnquiriesManager />} />
            <Route path="products" element={<ProductManager />} />
            <Route path="assets" element={<AssetLibrary />} />
            <Route path="finish-types" element={<FinishTypesEditor />} />
            <Route path="pricing" element={<PricingEditor />} />
            <Route path="color-studio" element={<ColorStudio />} />
            <Route path="viewer-pipeline" element={<ViewerPipelineEditor />} />
            <Route path="lighting" element={<LightingControls />} />
            <Route path="materials" element={<MaterialPropertiesEditor />} />
            <Route path="curtain" element={<CurtainSettings />} />
            <Route path="seasonal-effects" element={<SeasonalEffectsEditor />} />
            <Route path="mobile" element={<MobileExperienceEditor />} />
            <Route path="accessibility" element={<AccessibilityEditor />} />
            <Route path="seo" element={<SEOSettingsEditor />} />
            <Route path="analytics" element={<AnalyticsEditor />} />
            <Route path="social" element={<SocialMediaEditor />} />
            <Route path="email-marketing" element={<EmailMarketingEditor />} />
            <Route path="performance" element={<PerformanceMonitor />} />
            <Route path="security" element={<SecuritySettingsEditor />} />
            <Route path="backup" element={<BackupRecoveryEditor />} />
            <Route path="settings" element={<SettingsEditor />} />
            <Route path="holding" element={<HoldingPageEditor />} />
            <Route path="import-colours" element={<ImportColours />} />
          </Route>

          <Route
            path="/"
            element={
              <Suspense
                fallback={<div className="min-h-[100dvh] bg-black" />}
              >
                <EntranceHeroRoute />
              </Suspense>
            }
          />
          <Route path="/preview" element={<Navigate to="/preview/home" replace />} />
          <Route path="/preview/:pageKey" element={<PublicPreviewRoute />} />
          <Route
            path="/prototype/vase-stage-3d"
            element={
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-black text-stone-300">
                    Loading 3D prototype...
                  </div>
                }
              >
                <VaseStage3DPrototypeRoute />
              </Suspense>
            }
          />
          <Route
            path="/:pageKey"
            element={
              holdingPageEnabled ? <Navigate to="/" replace /> : <PublicPageRoute />
            }
          />
          <Route
            path="*"
            element={
              holdingPageEnabled ? <Navigate to="/" replace /> : <Navigate to="/" replace />
            }
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
